<script setup>
import { computed } from 'vue'
import { getFormType, getInitials, formatCzk } from '../../../../../helpers.js'
import Avatar from 'primevue/avatar'

const props = defineProps({
    message: Object,
})

const reservation = computed(() => props.message.reservations)
const form = computed(() => reservation.value?.reservation_forms)
const formTypeName = computed(() => form.value?.form_type?.name)
const isRental = computed(() => ['rental', 'accommodation'].includes(formTypeName.value))

const totalSpend = computed(() => {
    const items = reservation.value?.bill_items ?? []
    const sum = items.reduce((acc, item) => acc + (item.unit_price_with_vat * item.amount), 0)
    return sum > 0 ? sum : null
})

const nights = computed(() => {
    if (!reservation.value?.date_start || !reservation.value?.date_end) { return null }
    const diff = new Date(reservation.value.date_end) - new Date(reservation.value.date_start)
    return Math.round(diff / (1000 * 60 * 60 * 24))
})
</script>

<template>
    <div
        class="rounded-3xl mt-4 mb-6 border shadow-md bg-white/70 dark:bg-black/40 backdrop-blur-md p-4 max-w-96 hover:shadow-xl hover:-translate-y-0.5 transition-all flex flex-col gap-4"
        :class="`border-${form?.theme?.color ?? 'blue'}-300`"
    >
        <!-- Hlavička: typ formuláře + název -->
        <div class="flex items-center gap-3 text-lg">
            <span
                v-tooltip.bottom="getFormType(form?.form_type?.name)?.label"
                class="text-white w-6 h-6 rounded-full flex justify-center items-center !text-xs shadow-md flex-shrink-0"
                :class="[`bg-${form?.theme?.color ?? 'blue'}-500`, getFormType(form?.form_type?.name)?.icon]"
            />
            <span class="font-medium truncate">{{ form?.name }}</span>
        </div>

        <div class="flex flex-col gap-3">

            <!-- RENTAL / ACCOMMODATION -->
            <template v-if="isRental">

                <!-- Pronajímaná položka -->
                <div v-if="reservation.rental_items" class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ formTypeName === 'accommodation' ? $t('rental.label_accommodation') : $t('rental.label_item') }}</span>
                    <span class="text-lg font-medium flex items-center gap-2">
                        <i class="opacity-70 text-base" :class="formTypeName === 'accommodation' ? 'pi pi-home' : 'pi pi-box'" />
                        {{ reservation.rental_items.name }}
                    </span>
                </div>

                <!-- Datum od–do -->
                <div class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_period') }}</span>
                    <span class="text-lg font-medium flex items-center gap-2">
                        <i class="pi pi-calendar opacity-70 text-base" />
                        {{ reservation.formatted_date }}
                        <span class="opacity-50">–</span>
                        {{ reservation.formatted_date_end }}
                    </span>
                    <span v-if="nights" class="text-sm opacity-60 mt-0.5 ml-7">
                        {{ nights }} {{ nights === 1 ? $t('rental.night_one') : nights < 5 ? $t('rental.night_few') : $t('rental.night_many') }}
                    </span>
                </div>

                <!-- Obsazenost -->
                <div v-if="reservation.adults || reservation.children || reservation.pets" class="flex items-center gap-3 text-sm">
                    <span class="text-xs uppercase opacity-60 font-medium w-full" style="margin-bottom: -8px">{{ $t('rental.label_occupancy') }}</span>
                </div>
                <div v-if="reservation.adults || reservation.children || reservation.pets" class="flex items-center gap-4">
                    <div v-if="reservation.adults" class="flex font-medium items-center gap-1.5">
                        <i class="pi pi-user opacity-60" />
                        <span>{{ reservation.adults }}</span>
                        <span>{{ $t('rental.label_adults') }}</span>
                    </div>
                    <div v-if="reservation.children" class="flex items-center gap-1.5">
                        <i class="pi pi-users opacity-60" />
                        <span class="font-medium">{{ reservation.children }}</span>
                        <span>{{ $t('rental.label_children') }}</span>
                    </div>
                    <div v-if="reservation.pets" class="flex items-center gap-1.5">
                        <i class="pi pi-heart opacity-60" />
                        <span class="font-medium">{{ reservation.pets }}</span>
                        <span>{{ $t('rental.label_pets') }}</span>
                    </div>
                </div>

            </template>

            <!-- PERSONAL / SERVICE -->
            <template v-else>

                <!-- Čas -->
                <div v-if="reservation.time_start || reservation.time_end" class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_time') }}</span>
                    <span class="text-lg font-medium flex items-center gap-2">
                        <i class="pi pi-clock opacity-70 text-base" />
                        {{ reservation.time_start?.substring(0, 5) ?? '—' }} – {{ reservation.time_end?.substring(0, 5) ?? '—' }}
                    </span>
                </div>

                <!-- Datum -->
                <div class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_date') }}</span>
                    <span class="text-lg font-medium flex items-center gap-2">
                        <i class="pi pi-calendar opacity-70 text-base" />
                        {{ reservation.formatted_date }}
                    </span>
                </div>

                <!-- Služba -->
                <div v-if="reservation.services?.name" class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_service') }}</span>
                    <span class="text-lg font-medium flex items-center gap-2">
                        <i class="pi pi-briefcase opacity-70 text-base" />
                        {{ reservation.services.name }}
                    </span>
                </div>

                <!-- Kolega -->
                <div v-if="reservation.workers?.name" class="flex flex-col">
                    <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_colleague') }}</span>
                    <div class="flex items-center gap-3 mt-1">
                        <Avatar :label="getInitials(reservation.workers.name)" shape="circle" class="shadow" />
                        <span class="text-lg font-medium">{{ reservation.workers.name }}</span>
                    </div>
                </div>

            </template>

            <!-- Celková částka (společné pro oba typy) -->
            <div v-if="totalSpend" class="flex flex-col border-t pt-3 mt-1">
                <span class="text-xs uppercase opacity-60 font-medium">{{ $t('rental.label_total') }}</span>
                <span class="text-xl font-bold flex items-center gap-2">
                    <i class="pi pi-wallet opacity-70 text-base" />
                    {{ formatCzk(totalSpend) }}
                </span>
            </div>

        </div>
    </div>
</template>
